// react.tsx — React wrapper around mount().
//
// Next.js App Router: add 'use client' to whichever file imports these.
// The widget needs WebSocket + DOM — it cannot render on the server.
//
// Import path: '@paramms/chat-widget/react'
//
// Two components:
//   ChatWidget        — general support chat (hotel, SaaS, helpdesk, etc.)
//   MarketplaceChat   — one thread per listing (used cars, rentals, etc.)
//
// Both: only url + profileId are required. userId is always optional —
// anonymous guests are handled automatically via localStorage.

import { useEffect, useRef, useState } from 'react'
import { mount, type MountOptions, type WidgetHandle } from './index.js'

// Inline SVG launcher glyphs — emoji ('💬'/'✕') render differently on every
// OS and clash with brand accents; these are crisp and consistent everywhere.
function ChatGlyph({ size = 26 }: { size?: number }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
        fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  )
}
function CloseGlyph({ size = 22 }: { size?: number }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

// ── Shared base props ─────────────────────────────────────────────────────────

interface BaseProps {
  /** Relay URL — set as NEXT_PUBLIC_RELAY_URL in .env. Any scheme works:
   *  `https://api.example.com` is fine. The WebSocket URL and REST base are
   *  derived automatically — you do NOT need to pass `wss://` or `/ws`. */
  url:       string
  /** HTTP(S) base for REST calls — only when the REST API is on a different
   *  origin than the socket. Normally omit. */
  apiUrl?:   string
  /** Your Relay profile ID — set as NEXT_PUBLIC_RELAY_PROFILE_ID in .env — required */
  profileId: string

  /** A signed identity token — an ES256 JWT `{sub,iat,exp}` minted by YOUR
   *  backend for chatrooms with signed identity (guestPublicKey) enabled.
   *  This is the production identity tier; wins over `userId`. */
  token?:      string
  /** Called when a signed token expires: return a fresh token from your
   *  backend to renew the session without a reload. */
  refreshToken?: () => Promise<string | null>
  /** Your logged-in user's stable ID. When omitted the widget automatically
   *  assigns a persistent anonymous ID from localStorage — no login required. */
  userId?:     string
  /** Shown to agents instead of the raw user ID */
  userName?:   string
  /** Shown to agents so they can follow up by email */
  userEmail?:  string
  /** Avatar URL shown in the widget header and dashboard */
  userAvatar?: string

  /** Brand colour hex, e.g. "#1a56db". Defaults to the profile theme colour. */
  accent?:   string
  /** Secondary accent (guest bubble + send button); follows `accent` when omitted. */
  accent2?:  string
  /** Colour scheme: 'auto'|'light'|'dark'. */
  theme?:    'auto' | 'light' | 'dark'
  /** Load Baloo 2 + Nunito webfonts (default true). */
  webfont?:  boolean
  /** Render as a floating launcher button instead of inline */
  launcher?: boolean
  /** Launcher position — default 'bottom-right' */
  position?: 'bottom-right' | 'bottom-left'
  /** Launcher teaser ("optional message" above the bubble). String or
   *  { title, subtitle }. Omit to use the chatroom's manifest value. */
  launcherMessage?: string | { title: string; subtitle?: string }
  /** Pre-set reply chips shown above the input */
  quickReplies?: string[]
  /** Container height when rendered inline. Default: '100%' */
  height?: string
  /** UI language ('ko', 'ko-KR', …). Omit to auto-detect from the browser. */
  locale?: MountOptions['locale']
  /** i18n string overrides for non-English sites */
  i18n?: MountOptions['i18n']
  /** Per-tenant feature switches (default all ON). Set a flag false to disable
   *  reactions, CSAT, or KB deflection. */
  features?: MountOptions['features']
  /** ISO language code for auto-translating incoming messages */
  translateLang?: string
  /** Show a back chevron in the chatroom header that opens the full conversation
   *  list (`ChatApp`) — turns a single-thread widget into a Channel.io-style app
   *  where you can jump to any of the user's other conversations and back. */
  inbox?: boolean
  /** Inbox scope when `inbox` is set: 'tenant' (default) lists the user's threads
   *  across ALL your chatrooms; 'profile' limits it to this one. */
  inboxScope?: 'tenant' | 'profile'
}

// ── ChatWidget ────────────────────────────────────────────────────────────────

export interface ChatWidgetProps extends BaseProps {
  /** Optional context card shown at the top of the chat
   *  (e.g. the support ticket, booking, or order being discussed) */
  contextTitle?:    string
  contextSubtitle?: string
  contextStatus?:   string

  /** Stable item ID — pins this conversation to a specific item/thread. */
  subjectId?: string
}

/**
 * General-purpose support chat widget. Only `url` and `profileId` are required.
 * All other props are optional — anonymous guests work without any configuration.
 *
 * @example Basic support chat
 * ```tsx
 * <ChatWidget
 *   url={process.env.NEXT_PUBLIC_RELAY_URL}
 *   profileId={process.env.NEXT_PUBLIC_RELAY_PROFILE_ID}
 *   userId={session?.user.id}
 *   userName={session?.user.name}
 *   userEmail={session?.user.email}
 * />
 * ```
 *
 * @example Multi-thread (tickets, bookings, orders) — one thread per subjectId
 * ```tsx
 * <ChatWidget
 *   url={...} profileId={...}
 *   subjectId={`ticket_${ticket.id}`}
 *   contextTitle={ticket.title}
 *   contextStatus={ticket.status}
 *   userId={session?.user.id}
 * />
 * ```
 *
 * Need a list of the user's threads with tap-to-open? That's `<ChatApp />`
 * (inline) or `<ChatAppLauncher />` (floating bubble) — this component renders
 * a single conversation.
 */
export function ChatWidget({
  url, apiUrl, profileId,
  token, refreshToken,
  userId, userName, userEmail, userAvatar,
  contextTitle, contextSubtitle, contextStatus,
  subjectId,
  accent, accent2, theme, webfont, launcher, position, launcherMessage, quickReplies, height = '100%',
  i18n, features, translateLang, inbox, inboxScope, locale,
}: ChatWidgetProps): JSX.Element {
  const ref       = useRef<HTMLDivElement>(null)
  const handleRef = useRef<WidgetHandle | null>(null)
  // When `inbox` is enabled, the header back chevron swaps this single thread
  // for the full conversation list (ChatApp). Tapping a row there opens that
  // thread in ChatApp's own stack; the widget becomes a mini messaging app.
  const [showInbox, setShowInbox] = useState(false)

  useEffect(() => {
    if (showInbox) return            // showing the list, not the single thread
    if (!ref.current) return
    handleRef.current?.close()
    handleRef.current = mount({
      el: ref.current,
      url,
      ...(apiUrl ? { apiUrl } : {}),
      profileId,
      ...(subjectId ? { subjectId }    : {}),
      ...(token        ? { token }        : {}),
      ...(refreshToken ? { refreshToken } : {}),
      ...(userId    ? { userId } : {}),
      ...(userId || userName || userEmail || userAvatar ? {
        user: {
          ...(userName   ? { name:   userName }   : {}),
          ...(userEmail  ? { email:  userEmail }  : {}),
          ...(userAvatar ? { avatar: userAvatar } : {}),
        },
      } : {}),
      ...(contextTitle ? {
        subject: {
          title: contextTitle,
          ...(contextSubtitle ? { subtitle: contextSubtitle } : {}),
          ...(contextStatus   ? { status:   contextStatus }   : {}),
        },
      } : {}),
      ...(quickReplies   ? { quickReplies }                                : {}),
      ...(accent         ? { accent }                                      : {}),
      ...(accent2        ? { accent2 }                                     : {}),
      ...(theme          ? { theme }                                       : {}),
      ...(webfont === false ? { webfont: false }                          : {}),
      ...(translateLang !== undefined ? { translateLang }                  : {}),
      ...(i18n           ? { i18n }                                        : {}),
      ...(locale         ? { locale }                                      : {}),
      ...(features        ? { features }                                    : {}),
      ...(inbox && !launcher ? { onBack: () => setShowInbox(true) }        : {}),
      ...(launcher       ? { launcher, position: position ?? 'bottom-right', ...(launcherMessage ? { launcherMessage } : {}) } : {}),
    })
    return () => { handleRef.current?.close(); handleRef.current = null }
    // Every prop READ inside this effect that can change at runtime must be
    // listed below, or changing it silently does nothing. `locale` was passed
    // to mount() but missing from the deps, so a host that switched languages
    // saw no change unless it ALSO changed `translateLang` (which was listed) —
    // the likely source of "the React embed must pass locale" confusion.
    // Object-valued props (i18n/features/quickReplies) are deliberately still
    // excluded: a new object identity on every render would remount the widget
    // in a loop. Hosts must remount to change those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, apiUrl, profileId, subjectId, token, refreshToken, userId, userName, userEmail, userAvatar, accent, accent2, theme, webfont, launcher, position, launcherMessage, translateLang, locale, contextTitle, contextSubtitle, contextStatus, inbox, showInbox])

  // launcher + inbox → a floating bubble that opens the full thread list.
  // mount()'s fixed single-thread panel can't host the list, so when both are
  // set we delegate to ChatAppLauncher (which owns the list↔room stack). This
  // makes `launcher` and `inbox` compose instead of inbox being ignored.
  if (launcher && inbox) {
    return (
      <ChatAppLauncher
        url={url} {...(apiUrl ? { apiUrl } : {})} profileId={profileId}
        {...(token ? { token } : {})} {...(refreshToken ? { refreshToken } : {})}
        {...(userId ? { userId } : {})} {...(userName ? { userName } : {})} {...(userEmail ? { userEmail } : {})}
        scope={inboxScope ?? 'tenant'} {...(accent ? { accent } : {})}
        floating position={position ?? 'bottom-right'}
      />
    )
  }

  // Inbox view: the full conversation list (its own list↔room stack). Inline
  // only — in launcher mode the chat lives in a fixed panel that mount() owns,
  // so use <ChatAppLauncher> for a launcher-with-inbox experience instead.
  if (inbox && !launcher && showInbox) {
    return (
      <ChatApp
        url={url} {...(apiUrl ? { apiUrl } : {})} profileId={profileId}
        {...(token ? { token } : {})} {...(refreshToken ? { refreshToken } : {})}
        {...(userId ? { userId } : {})} {...(userName ? { userName } : {})} {...(userEmail ? { userEmail } : {})}
        {...(accent ? { accent } : {})} {...(accent2 ? { accent2 } : {})} {...(theme ? { theme } : {})} {...(webfont === false ? { webfont: false } : {})} scope={inboxScope ?? 'tenant'} height={height}
        onClose={() => setShowInbox(false)}
      />
    )
  }

  // When launcher=true, still render the div — mount() moves it inside the
  // fixed panel above the bubble. Zero size so it doesn't affect page layout.
  return (
    <div
      ref={ref}
      style={launcher ? { width: 0, height: 0, overflow: 'hidden' } : { width: '100%', height, minHeight: height === '100%' ? '480px' : undefined }}
    />
  )
}

// ── MarketplaceChat ───────────────────────────────────────────────────────────

export interface MarketplaceChatProps extends BaseProps {
  /** Unique ID for this listing — each listing gets its own thread.
   *  Omit for a general (non-item-specific) conversation. */
  listingId?:     string
  /** Shown at the top of the chat — e.g. "2019 Toyota Camry SE" */
  listingTitle?:  string
  /** One-line detail — e.g. "45,000 km · Automatic" */
  listingMeta?:   string
  /** Price shown as a tag — e.g. 12500 → "$12,500" */
  listingPrice?:  number
  /** Status badge — e.g. "Available", "Sold", "Pending" */
  listingStatus?: string
}

/**
 * Marketplace chat widget. Two modes depending on whether `listingId` is provided:
 *
 * **With `listingId` (listing page)** — opens that listing's chat immediately.
 * No list screen. The buyer is already looking at the item so context is clear.
 * ```tsx
 * // On your listing detail page:
 * <MarketplaceChat
 *   url={process.env.NEXT_PUBLIC_RELAY_URL}
 *   profileId={process.env.NEXT_PUBLIC_RELAY_PROFILE_ID}
 *   listingId={car.id}
 *   listingTitle={car.title}
 *   listingPrice={car.price}
 *   listingMeta={`${car.mileage.toLocaleString()} km · ${car.gearbox}`}
 *   listingStatus="Available"
 *   userId={session?.user.id}
 *   userName={session?.user.name}
 *   userEmail={session?.user.email}
 * />
 * ```
 *
 * **Without `listingId`** — opens the buyer's single general (non-listing)
 * thread with this chatroom. For a "My Messages" / inbox page with the
 * WhatsApp-style thread list and tap-to-open, use `<ChatApp />` (or
 * `<ChatAppLauncher />` for a floating bubble):
 * ```tsx
 * // On your /messages page:
 * <ChatApp
 *   url={process.env.NEXT_PUBLIC_RELAY_URL}
 *   profileId={process.env.NEXT_PUBLIC_RELAY_PROFILE_ID}
 *   userId={session?.user.id}
 * />
 * ```
 *
 * Only `url` and `profileId` are required. All other props are optional.
 */
export function MarketplaceChat({
  url, apiUrl, profileId,
  token, refreshToken,
  listingId, listingTitle, listingMeta, listingPrice, listingStatus,
  userId, userName, userEmail, userAvatar,
  accent, accent2, theme, webfont, launcher, position, launcherMessage, quickReplies, height = '100%',
  i18n, features, translateLang, inbox, inboxScope, locale,
}: MarketplaceChatProps): JSX.Element {
  const ref       = useRef<HTMLDivElement>(null)
  const handleRef = useRef<WidgetHandle | null>(null)
  const [showInbox, setShowInbox] = useState(false)

  useEffect(() => {
    if (showInbox) return
    if (!ref.current) return
    // Coalesce rapid identity changes into a SINGLE mount. NextAuth's
    // `session?.user.id` is undefined on first render and resolves a tick later,
    // which would otherwise open an anonymous conversation and immediately
    // orphan it when the real id arrives — surfacing as "a new chat every time".
    // Deferring the mount lets a same-tick id change settle first. For a slower
    // (network) session fetch, gate rendering on the session status host-side
    // (e.g. NextAuth: don't render until `status !== 'loading'`).
    let cancelled = false
    const timer = setTimeout(() => {
      if (cancelled || !ref.current) return
      handleRef.current?.close()
      handleRef.current = mount({
        el: ref.current,
        url,
        ...(apiUrl ? { apiUrl } : {}),
        profileId,
        ...(listingId ? { subjectId: `listing_${listingId}` } : {}),
        ...(token        ? { token }        : {}),
        ...(refreshToken ? { refreshToken } : {}),
        ...(userId    ? { userId } : {}),
        ...(userId || userName || userEmail || userAvatar ? {
          user: {
            ...(userName   ? { name:   userName }   : {}),
            ...(userEmail  ? { email:  userEmail }  : {}),
            ...(userAvatar ? { avatar: userAvatar } : {}),
            ...(listingId  ? { meta: { listingId } } : {}),
          },
        } : {}),
        ...(listingTitle ? {
          subject: {
            title: listingTitle,
            ...(listingMeta   ? { subtitle: listingMeta }                       : {}),
            ...(listingPrice  ? { tags: [`$${listingPrice.toLocaleString()}`] } : {}),
            ...(listingStatus ? { status:   listingStatus }                     : {}),
          },
        } : {}),
        ...(quickReplies ? { quickReplies } : {}),
        ...(accent        ? { accent }                                         : {}),
        ...(accent2       ? { accent2 }                                        : {}),
        ...(theme         ? { theme }                                          : {}),
        ...(webfont === false ? { webfont: false }                             : {}),
        ...(translateLang ? { translateLang }                                  : {}),
        ...(i18n          ? { i18n }                                           : {}),
        ...(locale        ? { locale }                                         : {}),
        ...(features       ? { features }                                       : {}),
        ...(inbox && !launcher ? { onBack: () => setShowInbox(true) }          : {}),
        ...(launcher      ? { launcher, position: position ?? 'bottom-right', ...(launcherMessage ? { launcherMessage } : {}) } : {}),
      })
    }, 0)
    return () => { cancelled = true; clearTimeout(timer); handleRef.current?.close(); handleRef.current = null }
  // Only identity/target-affecting props trigger a remount — metadata like
  // userName/userEmail is sent once at mount and must NOT churn the connection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, apiUrl, profileId, listingId, token, userId, accent, accent2, theme, webfont, launcher, position, launcherMessage, translateLang, locale, inbox, showInbox])

  if (launcher && inbox) {
    return (
      <ChatAppLauncher
        url={url} {...(apiUrl ? { apiUrl } : {})} profileId={profileId}
        {...(token ? { token } : {})} {...(refreshToken ? { refreshToken } : {})}
        {...(userId ? { userId } : {})} {...(userName ? { userName } : {})} {...(userEmail ? { userEmail } : {})}
        scope={inboxScope ?? 'tenant'} {...(accent ? { accent } : {})}
        floating position={position ?? 'bottom-right'}
      />
    )
  }

  if (inbox && !launcher && showInbox) {
    return (
      <ChatApp
        url={url} {...(apiUrl ? { apiUrl } : {})} profileId={profileId}
        {...(token ? { token } : {})} {...(refreshToken ? { refreshToken } : {})}
        {...(userId ? { userId } : {})} {...(userName ? { userName } : {})} {...(userEmail ? { userEmail } : {})}
        {...(accent ? { accent } : {})} {...(accent2 ? { accent2 } : {})} {...(theme ? { theme } : {})} {...(webfont === false ? { webfont: false } : {})} scope={inboxScope ?? 'tenant'} height={height}
        onClose={() => setShowInbox(false)}
      />
    )
  }

  // When launcher=true, still render the div — mount() moves it inside the
  // fixed panel above the bubble. Zero size so it doesn't affect page layout.
  return (
    <div
      ref={ref}
      style={launcher ? { width: 0, height: 0, overflow: 'hidden' } : { width: '100%', height, minHeight: height === '100%' ? '480px' : undefined }}
    />
  )
}

// ── ChatList ──────────────────────────────────────────────────────────────────

/** Live viewport breakpoint — updates on resize/rotation via matchMedia,
 *  never a one-shot read (a one-shot read leaves layouts stuck after the
 *  user rotates their phone or resizes across the breakpoint). SSR-safe. */
function useIsMobile(maxWidthPx: number): boolean {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= maxWidthPx,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`)
    const onChange = (e: MediaQueryListEvent): void => setMobile(e.matches)
    setMobile(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [maxWidthPx])
  return mobile
}

export interface ChatAppProps {
  /** Relay URL — ONE url, any scheme; `https://api.relay.paramms.com` is the
   *  recommended form. The WebSocket URL and REST base are derived from it —
   *  you do NOT need to pass `wss://` or `/ws`. */
  url:        string
  /** HTTP(S) base for REST calls — only when the REST API is on a different
   *  origin than the socket. Normally omit.
   *  @deprecated pass a single `url`; kept for back-compat. */
  apiUrl?:    string
  /** A chatroom id. Provide `profileId` OR `tenantId` (see below). When both
   *  are given, profileId also fixes where the ✎ "new conversation" opens. */
  profileId?: string
  /** Tenant-level identification — the natural fit for a chat app: lists
   *  EVERY conversation this user has with your business across ALL of its
   *  chatrooms, without naming one. Use this when the platform runs several
   *  chatrooms under one tenant (e.g. a marketplace chatroom for
   *  `<MarketplaceChat/>` pages PLUS a general-support chatroom) — the inbox
   *  shows both, and each row opens against its own chatroom. The ✎ compose
   *  button opens the tenant's default chatroom (its oldest, as reported by
   *  the server). */
  tenantId?:  string
  /** A signed identity token (ES256 JWT) for chatrooms with signed identity
   *  enabled — the production tier. Wins over `userId`. */
  token?:     string
  /** Return a fresh token when a signed token expires. */
  refreshToken?: () => Promise<string | null>
  /** Your logged-in user's stable ID. Omit for anonymous (uses localStorage UID) */
  userId?:    string
  /** Optional: shown to agents in the dashboard */
  userName?:  string
  /** Optional: shown to agents in the dashboard */
  userEmail?: string
  /** Which conversations the list shows when configured with a profileId
   *  (default 'tenant'; irrelevant with `tenantId`, which is always
   *  tenant-wide):
   *    'tenant'  — every conversation this user has with the business that
   *      owns `profileId`, across ALL of its chatrooms — like a real
   *      messaging app. Each row opens against its own chatroom.
   *    'profile' — only this chatroom's threads (the pre-1.0.30 behaviour).
   *  Tenancy note: guests never see or send tenant ids — the server resolves
   *  the tenant FROM the profileId, so `profileId` stays the only id you
   *  configure. */
  scope?:     'profile' | 'tenant'
  /** Brand colour hex — default '#6c5ce7' */
  accent?:    string
  theme?:     'auto' | 'light' | 'dark'
  webfont?:   boolean
  /** Container height. Default: '100%' */
  height?:    string
  /** When provided, ChatApp renders an always-visible close (✕) control in the
   *  top-right corner of its container. Pass this whenever ChatApp is shown in
   *  an overlay/panel or as a mobile-fullscreen surface (ChatAppLauncher wires
   *  it automatically) so there is a reliable way OUT — without it, an inline
   *  fullscreen panel on a phone (no Escape key, no reachable backdrop) traps
   *  the user. Omit for a bare inline embed the host chromes itself. */
  onClose?:   () => void
  /** UI language ('ko', 'ko-KR', …). Omit to auto-detect from the browser. */
  locale?:    string
  /** i18n overrides */
  i18n?: import('./chatlist.js').ChatListOptions['i18n']
}

/**
 * Full chat-app component: conversation list on the left (or full screen on
 * mobile), chat room on the right when a thread is selected — like a
 * standalone messaging app. Tapping a thread opens the chat inline; the ✎
 * button starts a new conversation with the business.
 *
 * By default (`scope="tenant"`) the list shows EVERY conversation this user
 * has with the business that owns `profileId` — across all of its chatrooms —
 * and each row opens against its own chatroom. Pass `scope="profile"` to
 * limit it to one chatroom's threads.
 *
 * Identify at either level:
 *   • `tenantId`  — the natural fit for a chat app. A platform often runs
 *     SEVERAL chatrooms under one business (a marketplace chatroom for
 *     `<MarketplaceChat/>` pages plus a general-support chatroom, …); with
 *     `tenantId` the inbox shows the user's chats with all of them, no
 *     profileId needed. Compose opens the tenant's default chatroom.
 *   • `profileId` — any one of the business's chatrooms; still lists
 *     tenant-wide by default (`scope="tenant"`), and fixes where compose
 *     opens.
 *
 * @example
 * ```tsx
 * 'use client'
 * import { ChatApp } from '@paramms/chat-widget/react'
 *
 * export default function MessagesPage({ session }) {
 *   return (
 *     <div style={{ height: '100vh' }}>
 *       <ChatApp
 *         url={process.env.NEXT_PUBLIC_RELAY_URL}
 *         tenantId={process.env.NEXT_PUBLIC_RELAY_TENANT_ID}   // or profileId={…}
 *         userId={session?.user.id}
 *         userName={session?.user.name}
 *         userEmail={session?.user.email}
 *       />
 *     </div>
 *   )
 * }
 * ```
 */
export function ChatApp({
  url, apiUrl, profileId, tenantId, token, refreshToken, userId, userName, userEmail,
  accent, theme, webfont, height = '100%', i18n, onClose, locale,
  scope = 'tenant',
}: ChatAppProps): JSX.Element {
  const misconfigured = !profileId && !tenantId
  type Entry = import('./chatlist.js').ChatListEntry
  const [selected, setSelected] = useState<Entry | null>(null)
  /** The conversation-list module failed to load — show why instead of an
   *  empty panel. */
  const [listError, setListError] = useState(false)
  const listRef    = useRef<HTMLDivElement>(null)
  const chatRef    = useRef<HTMLDivElement>(null)
  const handleRef  = useRef<import('./chatlist.js').ChatListHandle | null>(null)
  const chatHandle = useRef<WidgetHandle | null>(null)

  // Build the chat list
  useEffect(() => {
    if (!listRef.current) return
    let cancelled = false
    import('./chatlist.js').catch((e: unknown) => {
      // This promise had no rejection handler at all: if the chunk failed to
      // load (CSP, a 404 on the split chunk, an offline navigation) the list
      // silently never mounted and the host saw an empty white panel with
      // nothing but an unhandled rejection in the console.
      console.error('[relay] could not load the conversation list module.', e)
      if (!cancelled) setListError(true)
      return null
    }).then((mod) => {
      if (!mod) return
      const { mountChatList } = mod
      // The dynamic import is async: if the effect was cleaned up (unmount,
      // dep change, or StrictMode's double-invoke) before it resolved, or the
      // ref is gone, don't mount a phantom list that never gets closed.
      if (cancelled || !listRef.current) return
      handleRef.current = mountChatList({
        el:        listRef.current,
        url,
        ...(apiUrl ? { apiUrl } : {}),
        ...(profileId ? { profileId } : {}),
        ...(tenantId  ? { tenantId }  : {}),
        ...(theme ? { theme } : {}),
        ...(webfont === false ? { webfont: false } : {}),
        scope,
        // We overlay a ✕ at top-right when `onClose` is given — tell the list to
        // keep its header clear so the ✕ doesn't land on the ✎ compose button.
        ...(onClose ? { reserveCloseSpace: true } : {}),
        onSelect:  (entry) => setSelected(entry),
        // Compose (✎) opens the chatroom's general support thread — without
        // this a user with no conversations yet had an empty list and no way
        // to ever start one, which is what made ChatApp feel broken.
        // Compose target: the configured chatroom, else the tenant's default
        // (oldest chatroom, reported by the server on the first list fetch).
        onNewChat: () => {
          const target = handleRef.current?.defaultProfileId()
          if (!target) return   // list hasn't loaded yet — nothing sane to open
          setSelected({ id: '__new__', profileId: target, state: 'open', updatedAt: Date.now() })
        },
        ...(token  ? { token }  : {}),
        ...(userId ? { userId } : {}),
        ...(accent ? { accent } : {}),
        ...(i18n   ? { i18n }   : {}),
        ...(locale ? { locale } : {}),
      })
    })
    return () => { cancelled = true; handleRef.current?.close(); handleRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, apiUrl, profileId, tenantId, token, userId, accent, locale, scope])

  // Open a ChatWidget when a thread is selected.
  //
  // Two correctness details here:
  //   • identity: `userId` goes through as userId (the host-vouched tier) and
  //     signed JWTs as `token` — the same tiers as every other component. It
  //     used to conflate them (`token: userId`), which worked only by the
  //     accident that mount() folds userId into the token, and left no way to
  //     pass a REAL signed token at all.
  //   • chatroom: the row's OWN profileId wins. With scope='tenant' the list
  //     spans every chatroom of the business, so opening against the mounted
  //     profileId would find-or-create a DIFFERENT conversation.
  useEffect(() => {
    if (!chatRef.current || !selected) return
    chatHandle.current?.close()
    chatHandle.current = mount({
      el:        chatRef.current,
      url,
      ...(apiUrl ? { apiUrl } : {}),
      profileId: (selected.profileId ?? profileId)!,   // rows always carry theirs; compose sets one explicitly
      // DM rows open as direct conversations (kind + peer). Opening them via
      // their dm:… subjectId only works for the participant that OWNS the
      // thread key — the other side would silently fork a duplicate.
      ...(selected.kind === 'direct' && selected.peerId
        ? { kind: 'direct' as const, peerId: selected.peerId }
        : selected.subjectId ? { subjectId: selected.subjectId } : {}),
      ...(token        ? { token }        : {}),
      ...(refreshToken ? { refreshToken } : {}),
      ...(userId ? { userId } : {}),
      ...(userId || userName || userEmail ? {
        user: {
          ...(userName  ? { name:  userName }  : {}),
          ...(userEmail ? { email: userEmail } : {}),
        },
      } : {}),
      ...(selected.subjectTitle ? {
        subject: {
          title: selected.subjectTitle,
          ...(selected.subjectMeta   ? { subtitle: selected.subjectMeta }   : {}),
        },
      } : {}),
      ...(accent ? { accent } : {}),
      ...(theme ? { theme } : {}),
      ...(webfont === false ? { webfont: false } : {}),
      ...(locale ? { locale } : {}),
      ...(i18n ? { i18n } : {}),
      // Stack navigation: the chatroom's back chevron returns to the list.
      onBack: () => setSelected(null),
    })
    // Keep the list fresh: opening a chat marks it read, and replies received
    // while a chat is open should update its row's preview/ordering.
    const listTimer = setInterval(() => handleRef.current?.refresh(), 20_000)
    return () => {
      clearInterval(listTimer)
      chatHandle.current?.close(); chatHandle.current = null
      // Refresh once on the way out so the row reflects what just happened.
      handleRef.current?.refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, accent, theme, webfont, locale])

  // (After all hooks — React requires unconditional hook order.)
  if (misconfigured) {
    return (
      <div style={{ padding: 16, font: '13px system-ui', color: '#b91c1c', background: '#fef2f2', borderRadius: 8 }}>
        [relay] &lt;ChatApp/&gt; needs a <code>profileId</code> or a <code>tenantId</code>.
      </div>
    )
  }

  // Stack navigation, like a native messaging app: show the LIST, and when a
  // conversation is tapped, push the CHATROOM over it (full surface); the
  // chatroom's own header carries the back chevron (wired via mount's onBack)
  // that pops back to the list. No two-pane "select a conversation" dead space —
  // that only ever made sense on a very wide desktop inbox, and read as broken
  // in the launcher panel / on mobile, which is how this is actually used.
  return (
    <div style={{ position: 'relative', width: '100%', height, minHeight: '400px', overflow: 'hidden', background: '#fff' }}>
      {onClose && !selected && (
        <button
          onClick={onClose}
          aria-label={i18n?.close ?? 'Close'}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 20,
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,.05)', border: 'none', color: '#1c1b1a',
            fontSize: 18, lineHeight: 1, cursor: 'pointer',
          }}
        >
          <CloseGlyph size={18} />
        </button>
      )}

      {/* List — kept mounted (just hidden) so its scroll position, search text
          and live unread counts survive a round-trip into a chatroom. */}
      <div style={{
        position: 'absolute', inset: 0,
        display: selected ? 'none' : 'flex', flexDirection: 'column',
      }}>
        {listError
          ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 16, font: '13px system-ui', color: '#b91c1c', textAlign: 'center' }}>
              {i18n?.error ?? 'Could not load conversations.'}
            </div>
          )
          : <div ref={listRef} style={{ width: '100%', height: '100%' }} />}
      </div>

      {/* Chatroom — only present while a conversation is selected. */}
      {selected && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
          <div ref={chatRef} style={{ width: '100%', height: '100%' }} />
        </div>
      )}
    </div>
  )
}

// ── ChatAppLauncher ───────────────────────────────────────────────────────────

export interface ChatAppLauncherProps {
  /** Relay URL — ONE url, any scheme; `https://api.relay.paramms.com` is the
   *  recommended form. The WebSocket URL and REST base are derived from it. */
  url:        string
  /** HTTP(S) base for REST calls — only when REST is on a different origin.
   *  @deprecated pass a single `url`; kept for back-compat. */
  apiUrl?:    string
  /** A chatroom id. Provide `profileId` OR `tenantId`. */
  profileId?: string
  /** Tenant-level identification — the panel lists EVERY conversation this
   *  user has with your business across ALL of its chatrooms (see
   *  ChatAppProps.tenantId). The ✎ compose opens the tenant's default
   *  chatroom. */
  tenantId?:  string
  /** A signed identity token (ES256 JWT) for chatrooms with signed identity
   *  enabled — the production tier. Wins over `userId`. */
  token?:     string
  /** Return a fresh token when a signed token expires. */
  refreshToken?: () => Promise<string | null>
  /** Your logged-in user's stable ID */
  userId?:    string
  userName?:  string
  userEmail?: string
  /** Which conversations the panel's list shows — see ChatAppProps.scope.
   *  Default 'tenant': every conversation this user has with the business. */
  scope?:     'profile' | 'tenant'
  /** Brand colour hex — default '#6c5ce7' */
  accent?:    string

  /** true  → floating bubble fixed to the corner of the screen (like Intercom)
   *  false → an inline "Messages" button that expands a panel below it
   *  Default: true */
  floating?:  boolean
  /** Only used when floating=true. Default: 'bottom-right' */
  position?:  'bottom-right' | 'bottom-left'
  /** Label shown on the button. Default: a chat-bubble icon for floating,
   *  'Messages' for inline. */
  label?:     string
  /** Width of the chat panel. Default: '420px' (floating) or '100%' (inline) */
  panelWidth?: string
  /** Height of the chat panel. Default: '600px' */
  panelHeight?: string
}

/**
 * A button that opens the full ChatApp (list → chat) in a panel.
 *
 * floating=true  → fixed bubble in the corner of the screen, like WhatsApp Web's
 *                  chat button or Intercom.
 * floating=false → an inline button (e.g. in your nav bar) that expands a panel
 *                  below it.
 *
 * @example Floating bubble (bottom-right)
 * ```tsx
 * <ChatAppLauncher
 *   url={process.env.NEXT_PUBLIC_RELAY_URL}
 *   profileId={process.env.NEXT_PUBLIC_RELAY_PROFILE_ID}
 *   userId={session?.user.id}
 *   floating
 *   position="bottom-right"
 * />
 * ```
 *
 * @example Inline nav button
 * ```tsx
 * <ChatAppLauncher
 *   url={process.env.NEXT_PUBLIC_RELAY_URL}
 *   profileId={process.env.NEXT_PUBLIC_RELAY_PROFILE_ID}
 *   userId={session?.user.id}
 *   floating={false}
 *   label="Messages"
 * />
 * ```
 */
export function ChatAppLauncher({
  url, apiUrl, profileId, tenantId, token, refreshToken, userId, userName, userEmail,
  scope,
  accent = '#6c5ce7',
  floating = true,
  position = 'bottom-right',
  label,
  panelWidth,
  panelHeight = '600px',
}: ChatAppLauncherProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const btnRef          = useRef<HTMLButtonElement>(null)

  const isRight = position === 'bottom-right'
  // Floating default is the inline SVG chat glyph (see ChatGlyph note); a
  // custom `label` string still wins. Inline mode defaults to a text label.
  const btnLabel = label ?? (floating ? undefined : 'Messages')
  const isMobile = useIsMobile(479)

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // ── Floating mode ──────────────────────────────────────────────────────────
  if (floating) {
    // Mobile (<480px): the panel must be its OWN fixed fullscreen layer
    // (inset:0). Sizing it 100vw INSIDE the bottom-right wrapper — as it used
    // to — anchored its right edge 20px in from the viewport, pushing the
    // whole app 20px off-screen. The bubble wrapper stays above it (z 9999
    // vs 9998) so the ✕ keeps working as the close control. Live breakpoint:
    // rotating the phone re-lays-out.
    return (
      <div style={{
        position: 'fixed',
        [isRight ? 'right' : 'left']: '20px',
        bottom: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isRight ? 'flex-end' : 'flex-start',
        gap: '12px',
      }}>
        {/* Panel */}
        <div style={{
          ...(isMobile ? {
            position: 'fixed' as const, inset: 0,
            width: '100%', height: '100dvh', borderRadius: '0', zIndex: 9998,
          } : {
            width:  `min(${panelWidth ?? '420px'}, calc(100vw - 40px))`,
            height: `min(${panelHeight}, calc(100dvh - 108px))`,
            borderRadius: '28px',
          }),
          overflow:      'hidden',
          boxShadow:     isMobile ? 'none' : '0 12px 32px rgba(108,92,231,.14)',
          background:    '#fff',
          display:       open ? 'flex' : 'none',
          flexDirection: 'column',
          transformOrigin: `bottom ${isRight ? 'right' : 'left'}`,
          animation:     open ? 'ocl-pop-in .18s ease' : 'none',
        }}>
          <ChatApp
            url={url} {...(apiUrl ? { apiUrl } : {})}
            {...(profileId ? { profileId } : {})}
            {...(tenantId  ? { tenantId }  : {})}
            {...(token        ? { token }        : {})}
            {...(refreshToken ? { refreshToken } : {})}
            {...(userId    ? { userId }    : {})}
            {...(userName  ? { userName }  : {})}
            {...(userEmail ? { userEmail } : {})}
            {...(scope     ? { scope }     : {})}
            accent={accent} height="100%"
            onClose={() => setOpen(false)}
            />
        </div>

        {/* Bubble button. On a fullscreen mobile panel the bubble sits at
            bottom-right ON TOP of the message composer/send button, so hide it
            while open on mobile — the in-panel top-right ✕ is the close there. */}
        <button
          ref={btnRef}
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close chat' : (typeof btnLabel === 'string' ? btnLabel : 'Open chat')}
          aria-expanded={open}
          style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: accent, color: '#fff', border: 'none',
            fontSize: '20px', cursor: 'pointer', flexShrink: 0,
            boxShadow: '0 12px 32px rgba(108,92,231,.14)',
            transition: 'transform .15s',
            alignItems: 'center', justifyContent: 'center',
            display: (open && isMobile) ? 'none' : 'flex',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)' }}
        >
          {open ? <CloseGlyph /> : (label ?? <ChatGlyph />)}
        </button>

        <style>{`@keyframes ocl-pop-in { from { opacity:0; transform:scale(.95) } to { opacity:1; transform:scale(1) } }`}</style>
      </div>
    )
  }

  // ── Inline mode ────────────────────────────────────────────────────────────
  // The panel is rendered via a fixed overlay anchored to the button position.
  // This prevents clipping from parent overflow:hidden containers (e.g. nav bars).
  // On mobile the anchored popover can't fit — it goes fullscreen instead
  // (the backdrop still closes it).
  const pWidth = `min(${panelWidth ?? '420px'}, calc(100vw - 16px))`

  // Track button position for panel anchor
  const [btnRect, setBtnRect] = useState<DOMRect | null>(null)

  const handleBtnClick = () => {
    if (!open && btnRef.current) setBtnRect(btnRef.current.getBoundingClientRect())
    setOpen(o => !o)
  }

  const panelTop   = btnRect ? btnRect.bottom + 8 : 0
  // Clamp so a button near the left edge can't push the panel off-screen.
  const panelRight = btnRect ? Math.max(8, window.innerWidth - btnRect.right) : 0

  return (
    <>
      {/* Trigger button */}
      <button
        ref={btnRef}
        onClick={handleBtnClick}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '8px 16px', borderRadius: '8px',
          background: open ? accent : 'transparent',
          color: open ? '#fff' : accent,
          border: `2px solid ${accent}`,
          fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          transition: 'background .15s, color .15s',
        }}
      >
        <ChatGlyph size={16} /> {btnLabel}
      </button>

      {/* Fixed panel — rendered at document root level via CSS fixed positioning */}
      {open && btnRect && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          />
          <div style={{
            position: 'fixed',
            ...(isMobile ? {
              inset: 0, width: '100%', height: '100dvh', borderRadius: '0',
            } : {
              top:   panelTop,
              right: panelRight,
              width: pWidth,
              height: `min(${panelHeight}, calc(100dvh - ${panelTop}px - 12px))`,
              borderRadius: '28px',
            }),
            overflow: 'hidden',
            boxShadow: isMobile ? 'none' : '0 12px 32px rgba(108,92,231,.14)',
            background: '#fff',
            zIndex: 9999,
            animation: 'ocl-slide-in .18s ease',
          }}>
            <ChatApp
              url={url} {...(apiUrl ? { apiUrl } : {})}
            {...(profileId ? { profileId } : {})}
            {...(tenantId  ? { tenantId }  : {})}
              {...(token        ? { token }        : {})}
              {...(refreshToken ? { refreshToken } : {})}
              {...(userId    ? { userId }    : {})}
              {...(userName  ? { userName }  : {})}
              {...(userEmail ? { userEmail } : {})}
              {...(scope     ? { scope }     : {})}
              accent={accent} height="100%"
              onClose={() => setOpen(false)}
              />
          </div>
          <style>{`@keyframes ocl-slide-in { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }`}</style>
        </>
      )}
    </>
  )
}
